// Floating inset card, matched to botriq-admin-fe's shared design (Sidebar.css).
// Flat nav, no groups or RBAC.
import {
  LayoutDashboard,
  Bot,
  Bell,
  Settings,
  LogOut,
  Megaphone,
  Store,
  Map,
  PanelLeftClose,
  PanelLeftOpen,
  X,
} from "lucide-react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { Dialog, DialogContent, Button, Box, Typography, Avatar } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { useAuth } from "../../auth/AuthProvider";
import { useState } from "react";
import { tokens } from "../../theme";
import "./Sidebar.css";

// Flat nav (no group subheadings). Company Profile is reached via Settings,
// so it's omitted here; Notifications stays (it also has the topbar bell).
const NAV_ITEMS = [
  { route: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { route: "/robots", label: "My Robots", icon: Bot },
  { route: "/venue", label: "Venues", icon: Store },
  { route: "/maps", label: "Maps", icon: Map },
  // Dispatch Delivery and Patrol Mode moved to a robot's Operations tab, which
  // scopes them to that robot. Both routes stay mounted.
  { route: "/advertisements", label: "Advertisements", icon: Megaphone },
  { route: "/notifications", label: "Notifications", icon: Bell },
  { route: "/settings", label: "Settings", icon: Settings },
];

const isRouteActive = (currentPath, route) => {
  if (route === "/dashboard") return currentPath === "/dashboard";
  return currentPath === route || currentPath.startsWith(route + "/");
};

const NavButton = ({ route, label, Icon, expanded, onNavigate, isActive }) => {
  const active = isActive(route);
  const btn = (
    <button
      type="button"
      onClick={() => onNavigate(route)}
      aria-current={active ? "page" : undefined}
      className={`bq-nav-item ${active ? "is-active" : ""} ${!expanded ? "is-collapsed" : ""}`}
    >
      <Icon size={17} strokeWidth={1.75} />
      {expanded ? (
        <span className="bq-nav-label">{label}</span>
      ) : (
        // Collapsed, the glyph is the only affordance and the tooltip below is
        // hover/focus-only, so the name has to come from here.
        <span className="bq-sr-only">{label}</span>
      )}
    </button>
  );
  if (!expanded)
    return (
      <Tooltip title={label} placement="right" arrow>
        {btn}
      </Tooltip>
    );
  return btn;
};

// Collapsing is a desktop affordance; in the mobile drawer the same control
// dismisses it instead, so glyph, name and target all differ.
const CollapseControl = ({ isMobile, expanded, onToggleSidebar, closeMobileNav }) => {
  let icon;
  let tooltip;
  if (isMobile) {
    icon = <X size={16} />;
    tooltip = "Close navigation";
  } else if (expanded) {
    icon = <PanelLeftClose size={16} />;
    tooltip = "Collapse sidebar";
  } else {
    icon = <PanelLeftOpen size={16} />;
    tooltip = "Expand sidebar";
  }

  return (
    <Tooltip title={tooltip} placement="right" arrow>
      <button
        type="button"
        className="bq-sidebar-collapse-btn"
        aria-label={isMobile ? "Close navigation" : "Toggle sidebar"}
        onClick={isMobile ? () => closeMobileNav?.() : onToggleSidebar}
      >
        {icon}
      </button>
    </Tooltip>
  );
};

CollapseControl.propTypes = {
  isMobile: PropTypes.bool,
  expanded: PropTypes.bool,
  onToggleSidebar: PropTypes.func,
  closeMobileNav: PropTypes.func,
};

const LogoutButton = ({ expanded, onRequestLogout }) => {
  const btn = (
    <button
      onClick={onRequestLogout}
      type="button"
      className={`bq-nav-item bq-logout ${!expanded ? "is-collapsed" : ""}`}
    >
      <LogOut size={17} strokeWidth={1.75} />
      {expanded ? (
        <span className="bq-nav-label">Log out</span>
      ) : (
        // Same reasoning as NavButton: collapsed, the glyph is the only
        // affordance and the tooltip never reaches a screen reader.
        <span className="bq-sr-only">Log out</span>
      )}
    </button>
  );
  if (expanded) return btn;
  return (
    <Tooltip title="Log out" placement="right" arrow>
      {btn}
    </Tooltip>
  );
};

LogoutButton.propTypes = {
  expanded: PropTypes.bool,
  onRequestLogout: PropTypes.func,
};

const LogoutDialog = ({ open, onCancel, onConfirm }) => (
  <Dialog
    open={open}
    onClose={onCancel}
    maxWidth="xs"
    fullWidth
    slotProps={{ paper: { sx: { borderRadius: "16px", overflow: "hidden" } } }}
  >
    <DialogContent sx={{ p: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          px: 3,
          pt: 4,
          pb: 3,
          gap: 1.5,
        }}
      >
        <Avatar sx={{ width: 52, height: 52, background: tokens.dangerSoft, color: tokens.danger }}>
          <LogoutIcon sx={{ fontSize: 26 }} />
        </Avatar>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: tokens.text, textAlign: "center" }}>
          Confirm Logout
        </Typography>
        <Typography
          sx={{
            fontSize: 12.5,
            color: tokens.text2,
            textAlign: "center",
            lineHeight: 1.5,
            maxWidth: 260,
          }}
        >
          Are you sure you want to logout from Botriq? Make sure to save any unsaved work.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, width: "100%", mt: 0.5 }}>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            onClick={onCancel}
            sx={{ borderColor: tokens.border, color: tokens.text, height: 36 }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="error"
            size="small"
            startIcon={<LogoutIcon sx={{ fontSize: "15px !important" }} />}
            onClick={onConfirm}
            sx={{ height: 36 }}
          >
            Logout
          </Button>
        </Box>
      </Box>
    </DialogContent>
  </Dialog>
);

LogoutDialog.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
};

export const Sidebar = ({
  sidebarOpen,
  onToggleSidebar,
  isMobile = false,
  mobileNavOpen = false,
  closeMobileNav,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  // On mobile the drawer is always the full rail — `sidebarOpen` means "docked
  // and expanded", a different question from "is the drawer showing".
  const expanded = isMobile ? true : sidebarOpen;

  const isActive = (route) => isRouteActive(location.pathname, route);

  // Navigating from the drawer has to dismiss it, or the overlay stays parked on
  // top of the very page that was just requested.
  const handleNavigate = (route) => {
    navigate(route);
    if (isMobile) closeMobileNav?.();
  };
  const handleConfirmLogout = () => {
    setOpenLogoutDialog(false);
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <>
      {/* Before the rail, so the rail's stacking context wins without a z-index
          fight. A <button> so dismissing is reachable from the keyboard. */}
      <button
        type="button"
        data-testid="sidebar-backdrop"
        aria-label="Close navigation"
        tabIndex={isMobile && mobileNavOpen ? 0 : -1}
        className={`bq-sidebar-backdrop ${isMobile && mobileNavOpen ? "is-open" : ""}`}
        onClick={() => closeMobileNav?.()}
      />

      <div
        data-testid="sidebar-rail"
        className={`bq-sidebar ${!expanded ? "is-collapsed" : ""} ${
          isMobile && mobileNavOpen ? "is-mobile-open" : ""
        }`}
        // Without this the off-canvas drawer's nav items stay in the tab order
        // while invisible.
        aria-hidden={isMobile && !mobileNavOpen ? "true" : undefined}
      >
        {/* The sidebar's own collapse control — the header carries no
            duplicate toggle. Brand lockup lives in the Header only. */}
        <div className="bq-sidebar-toolbar">
          {expanded && (
            <span className="bq-sidebar-toolbar-label">
              <span className="bq-sidebar-toolbar-dot" /> Navigation
            </span>
          )}
          <CollapseControl
            isMobile={isMobile}
            expanded={expanded}
            onToggleSidebar={onToggleSidebar}
            closeMobileNav={closeMobileNav}
          />
        </div>

        {/* Nav */}
        <nav className="bq-sidebar-nav">
          <div className="bq-nav-items">
            {NAV_ITEMS.map((item) => (
              <NavButton
                key={item.route}
                route={item.route}
                label={item.label}
                Icon={item.icon}
                expanded={expanded}
                onNavigate={handleNavigate}
                isActive={isActive}
              />
            ))}
          </div>
        </nav>

        {/* Footer */}
        <div className="bq-sidebar-footer">
          <LogoutButton expanded={expanded} onRequestLogout={() => setOpenLogoutDialog(true)} />
        </div>
      </div>

      <LogoutDialog
        open={openLogoutDialog}
        onCancel={() => setOpenLogoutDialog(false)}
        onConfirm={handleConfirmLogout}
      />
    </>
  );
};

Sidebar.propTypes = {
  sidebarOpen: PropTypes.bool,
  onToggleSidebar: PropTypes.func,
  isMobile: PropTypes.bool,
  mobileNavOpen: PropTypes.bool,
  closeMobileNav: PropTypes.func,
};
NavButton.propTypes = {
  route: PropTypes.string,
  label: PropTypes.string,
  Icon: PropTypes.elementType,
  expanded: PropTypes.bool,
  onNavigate: PropTypes.func,
  isActive: PropTypes.func,
};
